<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { ShoppingBag } from 'lucide-vue-next'
import CustomerOrderCard from './CustomerOrderCard.vue'

import type { CustomerOrder, CustomerOrdersMeta } from '@/types/customer/Order'

const PAGE_SIZE = 10

const props = defineProps<{
  customerId: string
}>()

const { t } = useI18n()
const notification = useNotification()
const { fetchCustomerOrders } = useCustomerOrders()

const orders = ref<CustomerOrder[]>([])
const meta = ref<CustomerOrdersMeta | null>(null)
const isLoading = ref(true)
const isLoadingMore = ref(false)
const loadMoreSentinel = ref<HTMLElement | null>(null)

const hasMore = computed(() => (meta.value ? meta.value.current_page < meta.value.last_page : false))

const loadPage = async (page: number) => {
  try {
    const response = await fetchCustomerOrders(props.customerId, { page, pageSize: PAGE_SIZE })
    orders.value = page === 1 ? response.data : [...orders.value, ...response.data]
    meta.value = response.meta
  } catch {
    notification.create('error', { text: t('pages.customer.customerDetailPage.orders.loadError') })
  }
}

const loadMore = async () => {
  if (!hasMore.value || isLoadingMore.value || !meta.value) {
    return
  }

  isLoadingMore.value = true
  await loadPage(meta.value.current_page + 1)
  isLoadingMore.value = false
}

useIntersectionObserver(
  loadMoreSentinel,
  ([entry]) => {
    if (entry?.isIntersecting) {
      loadMore()
    }
  },
  { rootMargin: '200px' }
)

onMounted(async () => {
  await loadPage(1)
  isLoading.value = false
})
</script>

<template>
  <section class="space-y-3">
    <template v-if="isLoading">
      <Skeleton v-for="index in 4" :key="index" class="h-[74px] w-full rounded-xl" />
    </template>

    <div
      v-else-if="orders.length === 0"
      class="flex flex-col items-center justify-center gap-2 rounded-xl border bg-card px-6 py-12 text-center"
    >
      <div class="flex size-12 items-center justify-center rounded-full bg-stone-100 text-muted-foreground">
        <ShoppingBag class="size-6" />
      </div>
      <p class="text-sm font-medium text-foreground">{{ t('pages.customer.customerDetailPage.orders.empty.title') }}</p>
      <p class="text-sm text-muted-foreground">{{ t('pages.customer.customerDetailPage.orders.empty.description') }}</p>
    </div>

    <template v-else>
      <CustomerOrderCard v-for="order in orders" :key="order.id" :order="order" />

      <div ref="loadMoreSentinel" class="h-px" />
      <div v-if="isLoadingMore" class="flex items-center justify-center py-4">
        <Spinner size="lg" />
      </div>
    </template>
  </section>
</template>
